import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { createGraphQLRequest } from "../../../../api/utils/httpRequest"
import type {
    SummaryNetCollectedBreakdownByEmployeeReportItem,
    SummaryNetCollectedBreakdownReportItem,
    SummaryNetCollectedByEmployeeReportItem,
    SummaryNetCollectedBySourceReportItem,
    SummaryNetRevenueByAssignmentReportItem,
    SummaryNetRevenueByCategoryReportItem,
    SummaryNetRevenueDailyItem,
    SummaryNetRevenueHourlyItem,
    SummaryReportRequestData,
    SummaryTransactionsByPaymentTypeReportItem,
} from "../../../../graphql/types"
import { SummaryNetCollectedBreakdownByPaymentTypeReportItem } from "../../../../graphql/types"

export const fetchNetCollectedBreakdownReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryNetCollectedBreakdownReportItem>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryNetCollectedBreakdownReport($data: SummaryReportRequestData!) {
                  summaryNetCollectedBreakdownReport(data: $data) {
                    grossRevenue
                    refundsTotal
                    cardSurcharge
                    serviceFee
                    cashDiscount
                    voidsTotal
                    specialPricings {
                        id
                        name
                        total
                    }
                    netRevenue
                    taxes {
                        rowNumber
                        name
                        percentage
                        total
                    }
                    tipsTotal
                    historicalRefunds
                    futureRefunds
                    netCollected
                    losses
                    forfeits
                    gratuityTotal
                    tipsAndGratuityTotal
                    tips {
                        id
                        name
                        amountTip
                    }
                  }
                }
            `,
            operationName: "summaryNetCollectedBreakdownReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryNetCollectedBreakdownReport) {
            return response.data.summaryNetCollectedBreakdownReport
        }

        return null
    })

export const fetchNetCollectedBreakdownByPaymentTypeReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryNetCollectedBreakdownByPaymentTypeReportItem>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryNetCollectedBreakdownByPaymentTypeReport($data: SummaryReportRequestData!) {
                  summaryNetCollectedBreakdownByPaymentTypeReport(data: $data) {
                     ...ReportItem
                     children {
                        ...ReportItem
                     }
                  }
                }
                
                fragment ReportItem on SummaryNetCollectedBreakdownByPaymentTypeReportItem {
                    paymentType
                    grossRevenue
                    refundsTotal
                    cardSurcharge
                    cashDiscount
                    serviceFee
                    voidsTotal
                    netRevenue
                    tipsTotal
                    historicalRefunds
                    futureRefunds
                    netCollected
                    losses
                    forfeits
                    gratuityTotal
                    tipsAndGratuityTotal  
                    specialPricings {
                        id
                        name
                        total
                    }        
                    taxes {
                        rowNumber
                        name
                        percentage
                        total
                    }   
                    tips {
                        id
                        name
                        amountTip
                    }      
                }
            `,
            operationName: "summaryNetCollectedBreakdownByPaymentTypeReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryNetCollectedBreakdownByPaymentTypeReport) {
            return response.data
                .summaryNetCollectedBreakdownByPaymentTypeReport as SummaryNetCollectedBreakdownByPaymentTypeReportItem
        }

        return null
    })

export const fetchTransactionsByPaymentTypeReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryTransactionsByPaymentTypeReportItem[]>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryTransactionsByPaymentTypeReport($data: SummaryReportRequestData!) {
                  summaryTransactionsByPaymentTypeReport(data: $data) {
                     ...ReportItem
                      children {
                        ...ReportItem
                        children {
                            ...ReportItem
                            children {
                                ...ReportItem
                            }
                        }
                      }
                  }
                }
                
                fragment ReportItem on SummaryTransactionsByPaymentTypeReportItem {
                     name
                     creditsCount
                     debitsCount
                     netCount
                     percentage
                     total
                }
            `,
            operationName: "summaryTransactionsByPaymentTypeReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryTransactionsByPaymentTypeReport) {
            return response.data.summaryTransactionsByPaymentTypeReport
        }

        return null
    })

export const fetchNetCollectedByEmployeeReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryNetCollectedByEmployeeReportItem[]>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryNetCollectedByEmployeeReport($data: SummaryReportRequestData!) {
                  summaryNetCollectedByEmployeeReport(data: $data) {
                     ...ReportItem
                      children {
                        ...ReportItem
                        children {
                            ...ReportItem
                            children {
                                ...ReportItem
                            }
                        }
                      }
                  }
                }
                
                fragment ReportItem on SummaryNetCollectedByEmployeeReportItem {
                    id
                    firstName
                    surname
                    cashTotal
                    cardTotal
                    externalTotal
                    total
                    redemptionTotal
                }
            `,
            operationName: "summaryNetCollectedByEmployeeReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryNetCollectedByEmployeeReport) {
            return response.data.summaryNetCollectedByEmployeeReport
        }

        return null
    })

export const fetchNetCollectedBreakdownByEmployeeReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryNetCollectedBreakdownByEmployeeReportItem[]>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryNetCollectedBreakdownByEmployeeReport($data: SummaryReportRequestData!) {
                  summaryNetCollectedBreakdownByEmployeeReport(data: $data) {
                     ...ReportItem
                      children {
                        ...ReportItem
                        children {
                            ...ReportItem
                            children {
                                ...ReportItem
                            }
                        }
                      }
                  }
                }
                
                fragment ReportItem on SummaryNetCollectedBreakdownByEmployeeReportItem {
                    id
                    firstName
                    surname
                    grossRevenueTotal
                    voidsTotal
                    refundsTotal
                    specialPricingsTotal
                    lossesTotal
                    forfeitedTotal
                    taxTotal
                    tipsTotal
                    total
                }
            `,
            operationName: "summaryNetCollectedBreakdownByEmployeeReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryNetCollectedBreakdownByEmployeeReport) {
            return response.data.summaryNetCollectedBreakdownByEmployeeReport
        }

        return null
    })

export const fetchNetRevenueByCategoryReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryNetRevenueByCategoryReportItem[]>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryNetRevenueByCategoryReport($data: SummaryReportRequestData!) {
                  summaryNetRevenueByCategoryReport(data: $data) {
                     ...ReportItem
                      children {
                        ...ReportItem
                        children {
                            ...ReportItem
                            children {
                                ...ReportItem
                                    children {
                                        ...ReportItem
                                    }
                            }
                        }
                      }
                  }
                }
                
                fragment ReportItem on SummaryNetRevenueByCategoryReportItem {
                    id
                    name
                    quantity
                    quantityPercentage
                    revenue
                    revenuePercentage
                    unit
                    unitQuantity
                }
            `,
            operationName: "summaryNetRevenueByCategoryReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryNetRevenueByCategoryReport) {
            return response.data.summaryNetRevenueByCategoryReport
        }

        return null
    })

export const fetchNetRevenueByAssignmentReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryNetRevenueByAssignmentReportItem[]>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryNetRevenueByAssignmentReport($data: SummaryReportRequestData!) {
                  summaryNetRevenueByAssignmentReport(data: $data) {
                     ...ReportItem
                      children {
                        ...ReportItem
                        children {
                            ...ReportItem
                            children {
                                ...ReportItem
                                    children {
                                        ...ReportItem
                                    }
                            }
                        }
                      }
                  }
                }
                
                fragment ReportItem on SummaryNetRevenueByAssignmentReportItem {
                    id
                    name
                    quantity
                    quantityPercentage
                    revenue
                    revenuePercentage
                }
            `,
            operationName: "summaryNetRevenueByAssignmentReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryNetRevenueByAssignmentReport) {
            return response.data.summaryNetRevenueByAssignmentReport
        }

        return null
    })

export const fetchNetCollectedBySourceReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryNetCollectedBySourceReportItem[]>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryNetCollectedBySourceReport($data: SummaryReportRequestData!) {
                  summaryNetCollectedBySourceReport(data: $data) {
                     ...ReportItem
                      children {
                        ...ReportItem
                        children {
                            ...ReportItem
                            children {
                                ...ReportItem
                                    children {
                                        ...ReportItem
                                    }
                            }
                        }
                      }
                  }
                }
                
                fragment ReportItem on SummaryNetCollectedBySourceReportItem {
                    id
                    name
                    count
                    countPercentage
                    total
                    totalPercentage
                }
            `,
            operationName: "summaryNetCollectedBySourceReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryNetCollectedBySourceReport) {
            return response.data.summaryNetCollectedBySourceReport
        }

        return null
    })

export const fetchNetRevenueDailyReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryNetRevenueDailyItem[]>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryNetRevenueDailyReport($data: SummaryReportRequestData!) {
                  summaryNetRevenueDailyReport(data: $data) {
                     ...ReportItem
                  }
                }
                
                fragment ReportItem on SummaryNetRevenueDailyItem {
                    date
                    netRevenueClosed
                    netRevenueOpened
                    orderCountOpened
                }
            `,
            operationName: "summaryNetRevenueDailyReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryNetRevenueDailyReport) {
            return response.data.summaryNetRevenueDailyReport
        }

        return null
    })

export const fetchNetRevenueHourlyReportData = (
    headers: Record<string, any>,
    data: SummaryReportRequestData
): Promise<Maybe<SummaryNetRevenueHourlyItem[]>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query summaryNetRevenueHourlyReport($data: SummaryReportRequestData!) {
                  summaryNetRevenueHourlyReport(data: $data) {
                     ...ReportItem
                  }
                }
                
                fragment ReportItem on SummaryNetRevenueHourlyItem {
                    createdOrders
                    day
                    hour
                    openOrders
                    revenueReceivable
                    revenueReceived
                    year
                }
            `,
            operationName: "summaryNetRevenueHourlyReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.summaryNetRevenueHourlyReport) {
            return response.data.summaryNetRevenueHourlyReport
        }

        return null
    })
